import type {IInput} from "../interfaces/IInput.interface";
import {InputDataTypes} from "../interfaces/enums/InputDataTypes.enum";
import {TransportationComponentErrorMessages} from "../../../frontend/js/interfaces/enums/TransportationComponentErrorMessages.enum";
import type {IOrderTrueDriverState} from "../store/states/IOrderTrueDriverState.interface";
import type {IOrderFalseDriverState} from "../store/states/IOrderFalseDriverState.interface";
import {
    CustomRouteFormTemplateComponentErrorMessages
} from "../interfaces/enums/CustomRouteFormTemplateComponentErrorMessages.enum";
import axios from "axios";
import NameOfHelper from "../helpers/nameOfHelper";

export default
class FormValidationService
{
    // private static emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    private static emailRegex = /^[\w-+!#\-_?\.]+@([\w-]+\.)+[\w-]{2,6}$/g;

    // At least 8 letters/ number/ symbols, at least 1 capital letter, 1 lowercase letter, 1 number, 1 symbol)
    // private static passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,32}$/;

    // At least 8 letters/ number/ symbols
    private static passwordRegex = /^[A-Za-z\d@$!%*?&]{8,32}$/;

    static validate(inputs: IInput[]): boolean {
        let isValidForm = true;
        let newPassword;

        for (const input of inputs) {
            let isValid = true;

            // Reset
            input.validation.state = true;
            input.validation.message = null;

            // Loop all proved rules
            for (const ruleProp of Object.getOwnPropertyNames(input.validation.rules)) {
                const rule = input.validation.rules[ruleProp];

                switch (ruleProp) {
                    case 'required':
                        switch (input.dataType) {
                            case InputDataTypes.TEXT:
                                isValid = input.value !== null && input.value.length > 0;
                                break;
                            case InputDataTypes.CHECKBOX:
                            case InputDataTypes.PASSWORD:
                            case InputDataTypes.TEL:
                                isValid = !!input.value;
                                break;
                        }

                        if (!isValid) {
                            input.validation.state = false;
                            input.validation.message = rule.message ? rule.message : 'input_required';

                            return isValid;
                        }
                        break;

                    case 'email':
                        if (!input.value) {
                            break;
                        }

                        if (!input.value.match(FormValidationService.emailRegex)) {
                            isValid = false;
                            input.validation.state = false;
                            input.validation.message = rule.message;
                        }
                        break;

                    case 'length':
                        if (!input.value) {
                            break;
                        }

                        if (rule.gt && input.value.length <= rule.gt) {
                            input.validation.state = false;
                            input.validation.message = rule.message;
                        }
                        break;
                    case 'password':
                        if (!input.value) {
                            break;
                        }

                        if (!input.value.match(FormValidationService.passwordRegex)) {
                            input.validation.state = false;
                            input.validation.message = rule.message ? rule.message : 'password_is_not_valid';
                            isValid = false;

                            return isValid;
                        }

                        newPassword = input.value;

                        break;
                    case 'existing_customer':
                        if (localStorage.getItem('customer.error_existing_customer') === 'true') {
                            input.validation.state = false;
                            input.validation.message = rule.message;
                            isValid = false;

                            return isValid;
                        }
                        break;
                    case 'password_match':
                        if (!input.value) {
                            break;
                        }

                        if (!newPassword || input.value !== newPassword) {
                            input.validation.state = false;
                            input.validation.message = rule.message ? rule.message : 'passwords_does_not_match';
                            isValid = false;

                            return isValid;
                        }

                        break;

                }

                if (!isValid) {

                    isValidForm = false;
                }
            }
        }
        return isValidForm;
    }



    // TODO ADDITIONAL REFACTOR VALIDATION ADAPT FOR ALL CASES

    static validateRentDateTime(state: IOrderFalseDriverState): boolean {
        state.messages!.validDate.state = true;
        state.messages!.validTime.state = true;

        let isValid = true;

        const pickupDate = state.pickupDate;
        const endDate = state.endDate;
        const pickupTime = state.pickupTime;
        const endTime = state.endTime;

        const today = new Date();
        const tomorrow = new Date().setDate(today.getDate() + 1);
        const tomorrowInMs = new Date(new Date(tomorrow).toDateString()).getTime();

        if ( !pickupTime || !endTime || !pickupDate || !endDate ) {
            return false;
        }

        const pickupDateInMs = new Date(new Date(pickupDate).toDateString()).getTime();
        const endDateInMs = new Date(new Date(endDate).toDateString()).getTime();

        // DATE VALIDATION
        if (pickupDateInMs > endDateInMs // Pickup has to be before end date
            || pickupDateInMs < tomorrowInMs // Later than today
        ) {
            state.messages!.validDate.state = false;
            isValid = false;
        }

        // TIME VALIDATION
        if (pickupDateInMs === endDateInMs) {

            const pickupTimeParts = pickupTime.split(':');
            const pickup = new Date(pickupDateInMs)
                .setHours(parseInt(pickupTimeParts[0]), parseInt(pickupTimeParts[1]));
            const pickupTimeInMs = new Date(pickup).getTime();

            const endTimeParts = endTime.split(':');
            const end = new Date(new Date(endDate).toDateString())
                .setHours(parseInt(endTimeParts[0]), parseInt(endTimeParts[1]));
            const endTimeInMs = new Date(end).getTime();


            if (pickupTimeInMs >= endTimeInMs
                || pickupTimeInMs + 60 * 60 * 1000 > end // At least one hour difference
            ) {
                state.messages!.validTime.state = false;
                isValid = false;
            }
        }

        return isValid;
    }

    static validateRentLocation(withDriver: boolean, store): boolean {
        const nameOf = (c) => NameOfHelper.obj(c);
        const state = ! withDriver ? store.state.orderFalseDriver : store.state.orderTrueDriver;

        let isValid = true;
        state.messages!.validLocation.state = true;

        let prop = ! withDriver ? nameOf(() => state.destination) : nameOf(() => state.pickup);

        if (! state[prop]) {
            state.messages!.validLocation.state = false;
            isValid = false;
        }

        return isValid;
    }

    static validateTransportation(payload: any, messages: any): boolean {

        // RESET MESSAGES
        for (const key of Object.keys(messages)) {
            messages[key].state = true;
            messages[key].message = null;
        }

        let isValid = true;
        const startLocation = payload.start;
        const destinationLocation = payload.destination;
        const startDate = payload.startDate;
        const endDate = payload.endDate;

        for (const key of Object.keys(messages)) {
            switch(key) {
                case 'validLocationStart':
                    if (!startLocation.city) {
                        messages[key].state = false;
                        messages[key].message = TransportationComponentErrorMessages.MESSAGE_START;

                        isValid = false;
                    }

                    break;

                case 'validLocationDestination':
                    if (!destinationLocation.city) {
                        messages[key].state = false;
                        messages[key].message = TransportationComponentErrorMessages.MESSAGE_DESTINATION;

                        isValid = false;
                    }

                    break;

                case 'validDate':

                    if (new Date(new Date(startDate).toDateString()).getTime() >= new Date(new Date(endDate).toDateString()).getTime()) {
                        messages[key].state = false;
                        messages[key].message = TransportationComponentErrorMessages.MESSAGE_DATE;
                        isValid = false;
                    }

                    break;

                default:
                    break
            }
        }

        if (
            (startLocation && destinationLocation) &&
            (startLocation === destinationLocation)
        ) {

            messages.validLocationStart.state = false;
            messages.validLocationDestination.state = false;

            messages.validLocationStart.message =
                messages.validLocationDestination.message =
                    TransportationComponentErrorMessages.MESSAGE_LOCATION_EQUAL;

            isValid = false;
        }

        return isValid;
    }

    static validateCustomRouteForm(payload: any, messages: any): boolean {
        // RESET MESSAGES
        for (const key of Object.keys(messages)) {
            messages[key].state = true;
            messages[key].message = null;
        }

        let validRoutes = true;
        let validRoute = true;
        let validTravelObjective = true;
        let validSelectedPredefinedOption = true;
        let validDistance = true;
        let validDuration = true;
        let validVehicle = true;

        for (const key of Object.keys(messages)) {

            switch (key) {
                case 'validRoutes':

                    // for (const route of payload.routes) {
                    //     if (!route.routePoint) {
                    //         validRoute = false;
                    //     }
                    // }

                    // if (!(payload.routes.length >= 2 && validRoute)) {
                    if (payload.routes.length < 2) {
                        messages[key].state = false;
                        messages[key].message = CustomRouteFormTemplateComponentErrorMessages.MESSAGE_ROUTES;
                        validRoutes = false;
                    }
                    break;

                case 'validTravelObjective':
                    if (!payload.travelObjective) {
                        messages[key].state = false;
                        messages[key].message = CustomRouteFormTemplateComponentErrorMessages.MESSAGE_TRAVEL_OBJECTIVE;
                        validTravelObjective = false;
                    }
                    break;

                case 'validSelectedPredefinedOption':
                    if (payload.selectedPredefinedOption === "choose") {
                        messages[key].state = false;
                        messages[key].message = CustomRouteFormTemplateComponentErrorMessages.MESSAGE_SELECTED_PREDEFINED_OPTION;
                        validSelectedPredefinedOption = false;
                    }
                    break;

                case 'validDistance':
                    if (!payload.distance) {
                        messages[key].state = false;
                        messages[key].message = CustomRouteFormTemplateComponentErrorMessages.MESSAGE_DISTANCE;
                        validDistance = false;
                    }
                    break;

                case 'validDuration':
                    if (!payload.duration.hours && !payload.duration.minutes) {
                        messages[key].state = false;
                        messages[key].message = CustomRouteFormTemplateComponentErrorMessages.MESSAGE_DURATION;
                        validDuration = false;
                    }
                    break;

                case 'validSelectedVehicle':
                    if(!payload.selectedVehicle) {
                        messages[key].state = false;
                        messages[key].message = CustomRouteFormTemplateComponentErrorMessages.MESSAGE_SELECTED_VEHICLE;
                        validVehicle = false;
                    }
                    break;
            }


        }

        return validRoutes && validTravelObjective && validSelectedPredefinedOption && validDistance && validDuration && validVehicle;


    }
}